import ActionsScreen from '@/karehero-app/actions/ActionsScreen';
import AppTab from '@/karehero-app/shell/AppTab';
import { RECIPIENT } from '@/karehero-app/tell-us-once/intake/content';

/**
 * The actions tab, as a tab: the bar under it and the care recipient named in
 * the bar above it.
 *
 * MainTabs gives a screen both when the app is running, and a flows page mounts
 * the screen on its own, so every mini here was a tab drawn without the things
 * that make it one.
 */
const tab = (props: Parameters<typeof ActionsScreen>[0] = {}) => (
  <AppTab active='actions'>
    <ActionsScreen title={RECIPIENT} {...props} />
  </AppTab>
);
import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import ActionCard from '@/components/wrapped/ActionCard';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

// A representative action for the component previews.
const PREVIEW_ACTION = {
  id: 99,
  title: 'Upload your bank statement',
  overdue: true,
  schedule: 'Due Sat 24th May · 16:00',
  who: 'OP',
  regarding: 'Regarding Mum’s care',
  createdBy: 'Abbie',
  assignedTo: 'Assigned to you',
  due: 'Due Sat 24th May · 16:00',
  kind: 'cam',
  done: false,
} as const;

// The member Actions tab handover, rendered live from the prototype. The
// CAM-side counterpart is the control panel's Actions tab.
export const actionsSpec: HandoverSpec = {
  id: 'actions',
  title: 'Actions',
  description:
    'The member’s action plan: everything they need to do for their loved one’s care, in one list. CAM-assigned actions arrive with notes, files and a linked call for context; members tick things off, open the done section to look back, and add their own actions and reminders.',
  links: [
    { label: 'Live prototype (Actions tab)', url: '/karehero-app/actions' },
    {
      label: 'Sign-up & onboarding handover',
      url: '/handover/onboarding',
    },
  ],
  flows: [
    {
      title: 'View and complete',
      noteAfter: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              No To do / Done pill. It made you choose between two lists when
              one of them is a handful of finished things you glance at.
            </span>
            <span>
              Done is nested away under the open work, with a count, shut until
              you ask for it.
            </span>
            <span>
              Opening it should show five and load more as you scroll. Not
              built: the prototype opens the lot, which is fine on a seed of two
              and wrong on a year of them.
            </span>
            <span>
              One week at a time, with arrows and a swipe either side of it.
              Arrows as well as the swipe, because a swipe nobody knows is there
              is not a way anywhere, and this gets read on a screen you cannot
              swipe.
            </span>
            <span>
              Overdue always shows, whichever week it belongs to. Something due
              last week that never got done is still the first thing on this
              week's list, in red. It only stops carrying forward once it is
              ticked off.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'list',
          title: 'Actions list',
          tap: 'Actions tab',
          render: () => tab(),
        },
        {
          id: 'detail',
          title: 'Action detail',
          tap: 'Tap an action card',
          render: () => tab({ initial: { detailId: 1 } }),
        },
        {
          id: 'done',
          title: 'Mark done',
          tap: 'Tick the checkbox, or ‘Mark as done’ in the detail',
          render: () => tab({ initial: { doneOpen: true } }),
        },
      ],
    },
    {
      title: 'Create an action',
      steps: [
        {
          id: 'create',
          title: 'Create action',
          tap: '‘Create action’ on the list',
          render: () => tab({ initial: { creating: true } }),
        },
        {
          id: 'create-repeats',
          title: 'Make it repeat',
          tap: 'Turn Repeats on',
          render: () => tab({ initial: { creatingRepeats: true } }),
        },
      ],
    },
    {
      title: 'Edit your own one-off action',
      steps: [
        {
          id: 'edit-own-detail',
          title: 'Your action',
          tap: 'Open an action you created — Edit and Delete appear',
          render: () => tab({ initial: { detailId: 3 } }),
        },
        {
          id: 'edit',
          title: 'Edit action',
          tap: 'Tap ‘Edit’: title, assignee, date, time',
          render: () => tab({ initial: { detailId: 3, editing: true } }),
        },
      ],
    },
    {
      title: 'Edit a repeating action',
      noteAfter: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Tapping Edit on a repeat asks what you mean before it opens a
              form. A one-off goes straight in, because there is nothing to ask.
            </span>
            <span>
              Editing this one changes its date. Editing the series changes how
              often it repeats, and the date that change takes effect from, so
              the occurrences already behind you are left alone.
            </span>
            <span>
              Neither form offers a repeat on/off switch. Turning a repeat on or
              off is not an edit, and treating it as one is where the awkward
              cases live: unrepeating a series with three occurrences behind it
              has to decide what happens to them, and nothing here needs it to.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'repeat-detail',
          title: 'Your repeating action',
          tap: 'Open a repeat you created',
          render: () => tab({ initial: { detailId: 8 } }),
        },
        {
          id: 'repeat-choice',
          title: 'This one, or the series',
          tap: '‘Edit’',
          render: () => tab({ initial: { detailId: 8, editing: true } }),
        },
        {
          id: 'repeat-series',
          title: 'Edit the series',
          tap: '‘Edit action series’: how often, and from when',
          render: () => tab({ initial: { detailId: 8, editingSeries: true } }),
        },
      ],
    },
    {
      title: 'Delete your own action',
      steps: [
        {
          id: 'delete',
          title: 'Confirm modal',
          tap: '‘Delete’ on your own action',
          render: () => tab({ initial: { detailId: 2, deleting: true } }),
        },
      ],
    },
  ],
  states: [
    {
      id: 'list-overdue',
      title: 'Something overdue',
      kind: 'happy',
      repro: 'Overdue rows sort to the top and read their date in red',
      render: () => tab(),
    },
    {
      id: 'week-next',
      title: 'Next week',
      kind: 'happy',
      repro: 'Forward a week. No overdue here, nothing can be late yet',
      render: () => tab({ initial: { week: 1 } }),
    },
    {
      id: 'week-last',
      title: 'Last week',
      kind: 'happy',
      repro:
        'Back a week. The bank statement sits in its own week here, and on this week as overdue',
      render: () => tab({ initial: { week: -1 } }),
    },
    {
      id: 'cam-action-detail',
      title: 'CAM action with full context',
      kind: 'happy',
      repro: 'Action 1: note, example file and linked call from Yasmin',
      render: () => tab({ initial: { detailId: 1 } }),
    },
    {
      id: 'repeating',
      title: 'A repeating action, set by a care expert',
      kind: 'happy',
      repro:
        'Action 7: set by a care expert to repeat weekly. Read-only like any CAM-set action, so no Edit and no chooser',
      render: () => tab({ initial: { detailId: 7 } }),
    },
    {
      id: 'repeating-own',
      title: 'A repeating action you set yourself',
      kind: 'happy',
      repro:
        'Action 8: the member’s own repeat, so Edit is there and opens the chooser rather than the form',
      render: () => tab({ initial: { detailId: 8 } }),
    },
    {
      id: 'cam-readonly',
      title: 'CAM-set action is read-only',
      kind: 'edge',
      repro:
        'Open a CAM-created action: no Edit or Delete. Members can only edit and delete actions they created themselves',
      render: () => tab({ initial: { detailId: 1 } }),
    },
    {
      id: 'delete-oneoff',
      title: 'Delete a one-off action',
      kind: 'edge',
      repro: 'Delete shows a single confirm',
      render: () => tab({ initial: { detailId: 3, deleting: true } }),
    },
    {
      id: 'empty',
      title: 'No actions yet',
      kind: 'empty',
      repro: 'initial={{ empty: true }} — first visit before any plan exists',
      render: () => tab({ initial: { empty: true } }),
    },
    {
      id: 'done-open',
      title: 'Done, opened',
      kind: 'happy',
      repro: 'The finished work, which is collapsed until you ask for it',
      render: () => tab({ initial: { doneOpen: true } }),
    },
  ],
  requirements: [
    {
      text: 'One list of open work, overdue first, each overdue row saying so itself',
      status: 'built',
      where: 'Actions list',
      note: 'Was grouped under Overdue and Upcoming headings. Upcoming only ever meant "not overdue", so the headings were a table of contents for a page you can already see. The row carries it now: the date reads "Overdue Sat 24th May" in red instead of "Due".',
    },
    {
      text: 'Done collects in a collapsed section under the open work, with a count',
      status: 'built',
      where: 'Actions list',
      note: 'Was an All / To do / Done segmented control. A pill bar made you choose between two lists when one of them is a handful of finished things you glance at, and All was the other two plus noise.',
    },
    {
      text: 'Repeating actions: set by a care expert, or made by the member on the create sheet',
      status: 'built',
      where: 'Make it repeat',
      note: 'Turning Repeats on expands an every-n-units schedule and Date becomes Starts. Editing an existing repeat is a separate question — the detail carries the this-one-or-the-series choice, and nothing in the prototype reaches it.',
    },
    {
      text: 'List shows one week at a time, moved by arrows or a swipe',
      status: 'built',
      where: 'Actions list',
    },
    {
      text: 'Overdue carries forward into the current week whatever week it is due in',
      status: 'built',
      where: 'Actions list',
      note: 'Shown on the week you are on, not pinned to every week: on a past week everything is already behind, and on a future one nothing can be late yet.',
    },
    {
      text: 'Done opens five at a time, loading more on scroll',
      status: 'not-built',
      where: 'Actions list',
      note: 'Opening Done renders everything in it. Fine on a seed of two, wrong on a year of them.',
    },
    {
      text: 'Action detail: note, attached files, linked call, created-by and assignment',
      status: 'built',
      where: 'Action detail',
    },
    {
      text: 'Mark done from the card checkbox or the detail sheet',
      status: 'built',
      where: 'Mark done',
    },
    {
      text: 'Member-created actions',
      status: 'built',
      where: 'Create action',
      note: 'Create is UI-only; nothing persists in the prototype',
    },
    {
      text: 'Only the creator can edit or delete an action — CAM-set actions are read-only for members',
      status: 'built',
      where: 'CAM-set action is read-only',
    },
    {
      text: 'Delete confirms in a floating modal',
      status: 'built',
      where: 'Delete',
    },
    {
      text: 'Notifications / reminders for due and overdue actions',
      status: 'not-built',
      note: 'Needs push infrastructure; out of prototype scope',
    },
    {
      text: 'Assigning an action to another care circle member',
      status: 'out-of-scope',
      note: 'Later, with care circle roles',
    },
  ],
  components: {
    mui: ['AppBar', 'Avatar', 'Checkbox', 'Chip', 'TextField', 'Typography'],
    existing: ['ActionCard', 'BottomSheet', 'StatusBar', 'Icon (llama)'],
    new: ['ActionsScreen', 'ActionDetail', 'CreateAction', 'EditAction'],
  },
  componentPreviews: {
    ActionCard: () => (
      <ActionCard
        action={PREVIEW_ACTION as never}
        onToggle={noop}
        onOpen={noop}
      />
    ),
  },
};
